// src/components/Sidebar/SidebarCardType.tsx
"use client";
import { useEffect } from "react";
import Link from "next/link";
import { ChevronDown, Plus } from "lucide-react";
import { useRecordContext } from "@/contexts/RecordContext";
import { CARD_LABELS } from "@/constants/cards";
import { computeCardCapabilities } from "@/lib/planMap";
import type { CardType } from "@/types/Plan";

type SidebarCardTypeProps = {
  openSection: string | null;
  setOpenSection: (section: string | null) => void;
  disabled?: boolean; // ← 外側のガード（無料プラン／テキスト式選択中など、UIを不活性にする理由全般）
  showPaidBanner?: boolean; // ← 「有料プランでご利用いただけます」を出すかどうか（課金が理由のときだけtrue）
};

export default function SidebarCardType({ openSection, setOpenSection, disabled, showPaidBanner }: SidebarCardTypeProps) {
  const recordContext = useRecordContext();

  if (!recordContext || recordContext.userProfileLoading || !recordContext.userProfile) {
    return null;
  }

  const { selectedCardType, setSelectedCardType, userProfile, log } = recordContext;

  // ✅ ここで権限を一元計算（override中は5種＆切替可 / singleは1種など）
  const caps = computeCardCapabilities(userProfile);

  // 🆕 「版を追加する」リンクの表示判定
  // 2026-08-04訂正：従来は有料・トライアル中（＝全版デジタル解放済み）は非表示にしていたが、
  // CEO承認（/editionsのデジタル権利と物理購入状況の分離）により、有料会員にも「物理は
  // 未購入」の版への購入導線を/editionsが表示するようになったため、常に表示する形に変更。
  const showAddEditionLink = true;
  // 2026-10-07：コース購入で追加されたカードタイプ（extraAllowedCardTypes）もここでマージする。
  // 従来はcaps.allowedのみを見ていたため、/courses/essentialsのゲートが"standard"へ切り替えると
  // 下のuseEffectが「許可外」と判断してfreeへ戻し、ゲートが再び切り替える…という往復が起き、
  // そのたびに/api/user-profileへPOSTが飛んで「カードタイプ変更の通信に失敗しました」が出ていた。
  // PlayArea.tsx・RecordContextのcardCapsと同じマージ（加算のみ・購入者がいなければ変化なし）。
  const extraAllowed = recordContext.extraAllowedCardTypes ?? [];
  const accessibleCardTypes: CardType[] =
    extraAllowed.length === 0
      ? caps.allowed
      : (Array.from(new Set([...caps.allowed, ...extraAllowed])) as CardType[]);
  const canSwitch = caps.can_switch;
  const selectedComputed = caps.selected;

  const resolvedSelected: CardType = selectedCardType ?? selectedComputed;

  // ✅ ローカル選択が権限外なら、計算済みの選択値に丸める
  useEffect(() => {
    if (!selectedCardType || !accessibleCardTypes.includes(selectedCardType)) {
      setSelectedCardType(selectedComputed);
    }
  }, [accessibleCardTypes, selectedCardType, selectedComputed, setSelectedCardType]);

  const isOpen = openSection === "cardType";
  const handleToggle = () => {
    if (!disabled) setOpenSection(isOpen ? null : "cardType");
  };

  const handleSelectCardType = (type: CardType) => {
    if (type === resolvedSelected) return;

    log("card_type_change", {
      from: resolvedSelected,
      to: type
    });

    setSelectedCardType(type);
  };

  return (
    <>
    <nav className={`border-t pt-4 ${disabled ? "opacity-50 pointer-events-none select-none" : ""}`}>
      <h2
        className={`text-sm font-bold mb-2 cursor-pointer flex justify-between items-center ${
          disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700"
        }`}
        onClick={handleToggle}
      >
        カードタイプ
        <ChevronDown size={14} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"} />
      </h2>

      {isOpen && (
        <div className="flex flex-col gap-2">
          {/* Freeのみの縮退表示 */}
          {accessibleCardTypes.length === 1 && accessibleCardTypes[0] === "free" ? (
            <span className="px-2 py-1 rounded text-sm bg-gray-100">
              {CARD_LABELS["free"]?.ja || "フリー"}
            </span>
          ) : canSwitch ? (
            // ✅ 切替可：一覧＋選択
            accessibleCardTypes.map((type) => (
              <button
                key={type}
                onClick={() => handleSelectCardType(type)}
                className={`px-2 py-1 rounded text-left text-sm transition ${
                  (resolvedSelected === type)
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {CARD_LABELS[type]?.ja || type}
              </button>
            ))
          ) : (
            // ✅ 切替不可：現在の1種のみ（Singleやロック期間中など）
            <button
              key={"sidebar-card-types"}
              className={`px-2 py-1 rounded text-left text-sm transition bg-blue-500 text-white`}
              disabled
            >
              {CARD_LABELS[(resolvedSelected ?? "") as any]?.ja
                || resolvedSelected}
            </button>
          )}

          {/* --- 有料プラン誘導バナー（外側からの抑止用） ---
              2026-07-13修正：以前はdisabled（＝テキスト式選択中も含む）で出していたため、
              有料ユーザーがテキスト式に切り替えただけで誤って表示されるバグがあった
              （Kazuto実機報告）。課金が理由のときだけ表示するshowPaidBannerに分離。 */}
          {showPaidBanner && (
            <div className="mt-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-bold">
              この機能は有料プランでご利用いただけます
            </div>
          )}
        </div>
      )}
    </nav>

    {/*
      🆕 カードタイプを追加するリンク（サブスクなし・全版trial無効のユーザーのみ）
      ⚠️ 上の<nav>の外に置くこと。disabled時は<nav>にpointer-events-noneがかかり
      セクションの開閉自体もできなくなるため、内側に置くとリンクへ到達不能になる
      （D30失効後・物理版のみ所持のユーザーがまさにこの状態＝一番リンクが必要な相手）。
    */}
    {showAddEditionLink && (
      <Link
        href="/editions"
        className="mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition"
      >
        <Plus size={12} />
        カードタイプを追加する
      </Link>
    )}
    </>
  );
}
